/**
 * The records a conversation refers to, read from its typed message parts (owner 2026-10-31: the strategies, backtests,
 * artifacts and decisions a conversation works on open inside it, in a side panel, instead of in the detailed view).
 *
 * Only typed parts name a record: a card that shows ONE record (`entity_detail`, a strategy's detail, an approval's
 * decision) and an `artifact_ref` (its kind names the type: an artifact, a member's thread, a task …). Tool results are free-form and are never read
 * here. A list card's rows each name a record too, but a search can list fifty, so rows are not the conversation's
 * references — each row only opens its own record (`entityRefOfListRow`).
 *
 * `entity` is the card's own entity type (`strategy`, `artifact`, `decision`, `conversation`, …); a client panel type
 * of the same name renders it.
 */
import type { ConversationMessagePart } from '../conversation-interactions';
import type { Card } from './cards';

export interface ConversationEntityRef {
  /** The entity type, as cards name it (`entity_detail.entity`). */
  entity: string;
  /** The record's id as the card carries it (a strategy's is `def:<id>`). */
  id: string;
  title: string;
  subtitle?: string;
}

/** The entity type of a member's thread, linked from its answer (`artifact_ref` of kind `conversation`). */
export const CONVERSATION_ENTITY = 'conversation';

type ListCard = Extract<Card, { type: 'entity_list' | 'strategy_list' }>;
type ListRow = ListCard['items'][number];

/** The one record a card shows, or null for a card that shows none (a list, a question, progress). */
export function entityRefOfCard(card: Card): ConversationEntityRef | null {
  switch (card.type) {
    case 'entity_detail':
      return {
        entity: card.entity,
        id: String(card.entityId),
        title: card.title,
        ...(card.subtitle ? { subtitle: card.subtitle } : {}),
      };
    case 'strategy_detail':
      return {
        entity: 'strategy',
        id: String(card.strategyId),
        title: card.name,
        subtitle: [card.asset, card.timeframe].filter(Boolean).join(' · '),
      };
    case 'approval_request':
      return card.decisionRef.kind === 'daemon_decision'
        ? { entity: 'decision', id: String(card.decisionRef.decisionId), title: card.title }
        : { entity: 'artifact', id: String(card.decisionRef.artifactId), title: card.title };
    default:
      return null;
  }
}

/** The record one row of a list card names. */
export function entityRefOfListRow(card: ListCard, row: ListRow): ConversationEntityRef {
  if (card.type === 'strategy_list' && 'name' in row) {
    return {
      entity: 'strategy',
      id: String(row.id),
      title: row.name,
      subtitle: [row.asset, row.timeframe].filter(Boolean).join(' · '),
    };
  }
  const title = 'title' in row ? row.title : String(row.id);
  const subtitle = 'subtitle' in row ? row.subtitle : undefined;
  return {
    entity: card.type === 'entity_list' ? card.entity : 'strategy',
    id: String(row.id),
    title,
    ...(subtitle ? { subtitle } : {}),
  };
}

/** The records one message part refers to. */
export function entityRefsOfPart(part: ConversationMessagePart): ConversationEntityRef[] {
  if (part.type === 'card') {
    const ref = entityRefOfCard(part.card);
    return ref ? [ref] : [];
  }
  if (part.type === 'artifact_ref') {
    // The link's kind is the record's type (`artifact`, `conversation` for a member's thread, `task`, `project`, …).
    return [
      {
        entity: part.kind,
        id: String(part.artifactId),
        title: part.title,
        ...(part.summary ? { subtitle: part.summary } : {}),
      },
    ];
  }
  return [];
}

/** The key two references to the same record share. */
export function entityRefKey(ref: Pick<ConversationEntityRef, 'entity' | 'id'>): string {
  return `${ref.entity}:${ref.id}`;
}
